import React, {useEffect, useMemo, useState} from "react";
import ReactApexChart from "react-apexcharts";
import TableHeader from "../../components/Common/TableHeader";
import InfoCard from "../../components/Common/InfoCard";
import LegendTable from "../../components/Common/LegendTable";
import axios from "axios";
import {useThunk} from "../../hooks/useThunk";
import {deleteProperty} from "../../store";
import {fetchChart, fetchStats} from "../../store/thunks/InsightsThunks";
import { useSearchParams } from "react-router-dom";


export default function Insights() {
    const [searchParams, setSearchParams] = useSearchParams();

  const getCurrentMonth = () => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[new Date().getMonth()];
  };

    const dateRange = searchParams.get("dateRange") || getCurrentMonth();

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
  });
  const [stats, setStats] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
    const [selectedRange, setSelectedRange] = useState(dateRange);
  
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [doFetchStats, isFetchStats] = useThunk(fetchStats);
  const [doFetchChart] = useThunk(fetchChart);

  const handleDateChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

    useEffect(() => {
    if (selectedRange) {
      setSearchParams({ dateRange: selectedRange });
    }
  }, [selectedRange, setSearchParams]);

    useEffect(() => {
        const fetchStatsData = async () => {
                const response = await doFetchStats({ selectedMonth });
          if (response?.response?.data) {
                setStats(response.response.data);
          }
        };
        fetchStatsData();
    }, [selectedMonth, doFetchStats]);

    useEffect(() => {
        const fetchChartData = async () => {
      try {
        const res = await doFetchChart({ dateRange: selectedRange });

                const monthsData = res?.response?.data?.data || [];

                const formatted = monthsData.map((m) => ({
                    month: m.monthName,
                    userCount: Number(m.userCount || 0),
                    revenue: Number(m.revenue || 0),
                    profit: Number(m.profit || 0),
                    expectedRent: Number(m.expectedRent || 0),
                    collectedRent: Number(m.collectedRent || 0),
                    collectionRate: Number(m.collectionRate || 0),
                    totalProperties: Number(m.totalProperties || 0),
                    totalUnits: Number(m.totalUnits || 0),
                }));
                console.log("fetch stats2:", formatted);
                setRows(formatted);
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
        };
        fetchChartData();
    }, [selectedRange, doFetchChart]);

  useEffect(() => {
    if (selectedRange) {
      setSelectedMonth(selectedRange);
    }
  }, [selectedRange]);

    const handleSearch = async () => {
    setIsLoading(true);
    try {
      const chartResponse = await doFetchChart({ dateRange: selectedRange });
      const statsResponse = await doFetchStats({
        selectedMonth: selectedRange,
      });

      if (chartResponse?.response?.data?.data) {
        const monthsData = chartResponse.response.data.data;
        const formatted = monthsData.map((m) => ({
          month: m.monthName,
          userCount: Number(m.userCount || 0),
          revenue: Number(m.revenue || 0),
          profit: Number(m.profit || 0),
          expectedRent: Number(m.expectedRent || 0),
          collectedRent: Number(m.collectedRent || 0),
          collectionRate: Number(m.collectionRate || 0),
          totalProperties: Number(m.totalProperties || 0),
          totalUnits: Number(m.totalUnits || 0),
        }));
        setRows(formatted);
      }

      if (statsResponse?.response?.data) {
        setStats(statsResponse.response.data);
      }
    } catch (error) {
      console.error("Error during search:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = useMemo(
    () => rows.map((r) => r.month.slice(0, 3)),
    [rows]
  );
  const series = useMemo(
    () => [{ name: "New Landlords", data: rows.map((r) => r.userCount) }],
    [rows]
  );
    const Userseries = useMemo(
    () => [{ name: "New Landlords", data: rows.map((r) => r.userCount) }],
    [rows]
  );
  const Revenuecseries = useMemo(
    () => [{ name: "Revenue", data: rows.map((r) => r.revenue) }],
    [rows]
  );
  const Profitseries = useMemo(
    () => [{ name: "Profit", data: rows.map((r) => r.profit) }],
    [rows]
  );
  const yAxisMax = (arr) => {
    const maxVal = arr.reduce((m, v) => Math.max(m, v), 0);
    // If maxVal is 0, return 10 as minimum
    if (maxVal === 0) return 10;
    
    // Round up to nearest 10
    const rounded = Math.ceil((maxVal + 1) / 10) * 10;
    return rounded;
};

const yAxisMaxUsers   = useMemo(() => yAxisMax(rows.map((r) => r.userCount)), [rows]);
const yAxisMaxRevenue = useMemo(() => yAxisMax(rows.map((r) => r.revenue)),   [rows]);
const yAxisMaxProfit  = useMemo(() => yAxisMax(rows.map((r) => r.profit)),[rows]);
const baseBar = {
      chart: { type: "bar", height: 400, toolbar: { show: false } },
      plotOptions: {
        bar: { borderRadius: 10, horizontal: false, columnWidth: "64%" },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        title: {
          text: "Months",
          style: { fontSize: "13px", fontWeight: 700, color: "#000000B2" },
        },
        labels: {
          style: {
            fontSize: "12px",
            fontWeight: 700,
            colors: Array(categories.length).fill("#00000099"),
          },
        },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        labels: {
          formatter: (val) => `${val}`,
          style: { fontSize: "12px", fontWeight: 700, colors: "#00000099" },
        },
      },
      tooltip: {
        y: { formatter: (val) => `${val}` },
      },
      colors: ["#9250E5"],
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          gradientToColors: ["rgba(146, 80, 229, 0.25)"],
          stops: [0, 100],
          opacityFrom: 1,
          opacityTo: 1,
        },
      },
      grid: { strokeDashArray: 4 },
};


  const chartOptions = useMemo(
    () => ({
      ...baseBar,
      yaxis: {
        ...baseBar.yaxis,
        min: 0,
        max: yAxisMaxUsers,
      },
    }), [categories, yAxisMaxUsers]
);

  const RevenuechartOptions  = useMemo(() => ({ ...baseBar, yaxis: { ...baseBar.yaxis,
        min: 0, max: yAxisMaxRevenue,
        title: {
          text: "Revenue($)",
          style: { fontSize: "13px", fontWeight: 700, colors: ["#00000099"] },
        },
        labels: {
          formatter: (val) => `${val}`,
          style: { fontSize: "12px", fontWeight: 700, colors: ["#00000099"] },
        },
      },
    }),
    [categories, yAxisMaxRevenue]
  );
    const UserchartOptions  = useMemo(() => ({ ...baseBar, yaxis: { ...baseBar.yaxis,
        min: 0, max: yAxisMaxUsers,
        title: {
          text: "Users",
          style: { fontSize: "13px", fontWeight: 700, colors: ["#00000099"] },
        },
        labels: {
          formatter: (val) => `${val}`,
          style: { fontSize: "12px", fontWeight: 700, colors: ["#00000099"] },
        },
      },
    }),
    [categories, yAxisMaxUsers]
  );
  const ProfitchartOptions = useMemo(
    () => ({
      ...baseBar, yaxis: {
        ...baseBar.yaxis, min: 0, max: yAxisMaxProfit,
        title: {
          text: "Profit Trends($)",
          style: { fontSize: "13px", fontWeight: 700, colors: ["#00000099"] },
        },
        labels: {
          formatter: (val) => `${val}`,
          style: { fontSize: "12px", fontWeight: 700, colors: ["#00000099"] },
    } } }), [categories, yAxisMaxProfit]);


 const pct = (val, total) => (total ? `${Math.round((val / total) * 100)}%` : "0%");

  const totalUsers   = useMemo(() => rows.reduce((s, r) => s + r.userCount, 0), [rows]);
  const totalRevenue = useMemo(() => rows.reduce((s, r) => s + r.revenue, 0), [rows]);
  const totalProfit  = useMemo(() => rows.reduce((s, r) => s + r.profit, 0), [rows]);

  const legendData = useMemo(
    () =>
      rows.map((r) => ({
        label: r.month,
        value: r.userCount,
        percent: pct(r.userCount, totalUsers),
        color: "#A068E8",
      })),
    [rows, totalUsers]
  );

  const RevenuelegendData = useMemo(
    () =>
      rows.map((r) => ({
        label: r.month,
        value: r.revenue,
        percent: pct(r.revenue, totalRevenue),
        color: "#A068E8",
      })),
    [rows, totalRevenue]
  );

  const ProfitlegendData = useMemo(
    () =>
      rows.map((r) => ({
        label: r.month,
        value: r.profit,
        percent: pct(r.profit, totalProfit),
        color: "#A068E8",
      })),
    [rows, totalProfit]
  );

  const currentMonthData = useMemo(() => {
    return rows.find((row) => row.month === selectedRange) || {};
  }, [rows, selectedRange]);

  return (
    <div className="p-4 sm:p-6 md:p-[30px]">
      <div className="drop-shadow-[0px_4px_4px_0px_#0000000F]">
        <TableHeader
                    dateRangeFilter={{
            value: selectedRange,
            onChange: setSelectedRange,
            options: [
              { label: "January", value: "January" },
              { label: "February", value: "February" },
              { label: "March", value: "March" },
              { label: "April", value: "April" },
              { label: "May", value: "May" },
              { label: "June", value: "June" },
              { label: "July", value: "July" },
              { label: "August", value: "August" },
              { label: "September", value: "September" },
              { label: "October", value: "October" },
              { label: "November", value: "November" },
              { label: "December", value: "December" },
            ],
          }}
          buttons={[
            {
              label: "SEARCH",
              onClick: handleSearch,
              variant: "secondary",
              loading: isLoading,
              className:
                "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-4 rounded-[4px] w-[100px] uppercase font-medium",
            },
          ]}
          containerClass="bg-white flex items-center justify-start gap-6 p-3 sm:p-4 md:p-6 lg:pl-[31px] lg:py-[30px]"
          datePickerClass="gap-2"
          datePickerInputClass="w-full sm:w-full md:w-[296px] lg:w-[296px] [@media(min-width:1708px)]:w-[296px] border"
          buttonWrapperClass="justify-start md:justify-start gap-[10px]"
          buttonClass="uppercase tracking-wide flex justify-center bg-[#873FE2]"
        />
      </div>
      <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
        <div className="grid gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[52px] gap-y-[30px] grid-cols-1 xs:grid-cols-2 xl:grid-cols-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <InfoCard
              label="New Landlords"
              value={currentMonthData.userCount || stats?.userCount || 0}
              className="max-h-[100px] mx-auto"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Revenue"
              value={currentMonthData.revenue || stats?.revenue || 0}
              className="max-h-[100px] mx-auto"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-6"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Profit"
              value={currentMonthData.profit || stats?.profit || 0}
              className="max-h-[100px] mx-auto"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-6"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
        </div>
      </div>
      <div className="bg-white p-3 sm:p-5 md:p-6 lg:p-[30px] rounded-[10px]">
        <h2 className="text-[22px] leading-[100%] font-semibold font-inter text-[#4772F1] mb-[60px]">
          New landlords
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-9 md:gap-14 lg:gap-[84px] px-3 sm:px-5 md:px-6  lg:px-[30px]">
          <div className="w-full">
            <ReactApexChart
              options={UserchartOptions}
              series={Userseries}
              type="bar"
              height={340}
            />
          </div>

          <LegendTable
            data={legendData}
            showDot={false}
            showPercent={false}
            className="mt-4"
            classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
            classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
            classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
          />
        </div>
      </div>
      <div className="bg-white p-3 sm:p-5 md:p-6 lg:p-[30px] my-[30px] rounded-[10px]">
        <h2 className="text-[22px] leading-[100%] font-semibold font-inter text-[#4772F1] mb-[60px]">
          Revenue Trends
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-9 md:gap-14 lg:gap-[84px] px-3 sm:px-5 md:px-6  lg:px-[30px]">
          <div className="w-full">
            <ReactApexChart
              options={RevenuechartOptions}
              series={Revenuecseries}
              type="bar"
              height={340}
            />
          </div>

          <LegendTable
            data={RevenuelegendData}
            showDot={false}
            showPercent={false}
            className="mt-4"
            classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
            classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
            classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
          />
        </div>
      </div>
      <div className="bg-white p-3 sm:p-5 md:p-6 lg:p-[30px] rounded-[10px]">
        <h2 className="text-[22px] leading-[100%] font-semibold font-inter text-[#4772F1] mb-[60px]">
          Profit Trends
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-9 md:gap-14 lg:gap-[84px] px-3 sm:px-5 md:px-6  lg:px-[30px]">
          <div className="w-full">
            <ReactApexChart
              options={ProfitchartOptions}
              series={Profitseries}
              type="bar"
              height={340}
            />
          </div>

          <LegendTable
            data={ProfitlegendData}
            showDot={false}
            showPercent={false}
            className="mt-4"
            classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
            classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
            classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
          />
        </div>
      </div>
    </div>
  );
}
